import type { QueryHook } from "react-query-kit";
import type { AxiosResponse } from "axios";
import { useCallback, useMemo } from "react";

import type { IApiResponse, IAxiosFn } from "@/api";
import type {
  IReportDownloadEndpoint,
  IReportDownloadParams,
} from "@api/reports";
import {
  getAllPatientsReport,
  getAppointmentsReport,
  getDoctorsIncomeReport,
  getDoctorVisitCostReport,
  getExperimentsReport,
  getFollowUpReport,
  getHospitalIncomeReport,
  getImagesReport,
  getMedicalHistoryReport,
  getNoShowAppointmentsReport,
  getOrdersReport,
  getPrescriptionReport,
  getReferralReport,
  getTransactionsReport,
  useGetAllPatientsReport,
  useGetAppointmentsReport,
  useGetDoctorsIncomeReport,
  useGetDoctorVisitCostReport,
  useGetExperimentsReport,
  useGetFollowUpReport,
  useGetHospitalIncomeReport,
  useGetImagesReport,
  useGetMedicalHistoryReport,
  useGetNoShowAppointmentsReport,
  useGetOrdersReport,
  useGetPrescriptionReport,
  useGetReferralReport,
  useGetTransactionsReport,
} from "@api/reports";
import { downloadFile } from "@utils";
import { useCustomNotifyPromise } from "@/hooks/useNotifyPromise";
import {
  getMedicalServiceReport,
  useGetMedicalServiceReport,
} from "@api/reports/medicalServiceProvider";
import {
  getMedicalServiceCentersReport,
  useGetMedicalServiceCentersReport,
} from "@api/reports/medicalServiceCenters";
import {
  getMedicalServiceDoctorsReport,
  useGetMedicalServiceDoctorsReport,
} from "@api/reports/medicalServiceProviderDoctors";
import {
  getMedicalServiceAssignReport,
  useGetMedicalServiceAssignReport,
} from "@api/reports/medicalServiceAssignList";
import {
  getMedicalServiceServicesReport,
  useGetMedicalServiceServicesReport,
} from "@api/reports/medicalServiceServices";
import {
  getSystemAccountingReport,
  useGetSystemAccountingReport,
} from "@api/reports/systemAccounting";

export type IReportsType =
  | "allPatients"
  | "appointments"
  | "doctorsIncome"
  | "doctorVisitCost"
  | "followUp"
  | "hospitalIncome"
  | "noShowAppointments"
  | "prescription"
  | "referral"
  | "transactions"
  // | "visitSummaryNotes"
  | "orders"
  | "images"
  | "experiments"
  | "medical-history"
  | "medicalServiceProvider"
  | "medicalServiceCenters"
  | "medicalDoctors"
  | "medicalServiceAssign"
  | "medicalServiceServices"
  | "systemAccounting";

const _reportsHook: Record<
  IReportsType,
  QueryHook<
    AxiosResponse<IApiResponse<IReportDownloadEndpoint>>,
    IReportDownloadParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >
> = {
  allPatients: useGetAllPatientsReport,
  appointments: useGetAppointmentsReport,
  doctorsIncome: useGetDoctorsIncomeReport,
  systemAccounting: useGetSystemAccountingReport,
  doctorVisitCost: useGetDoctorVisitCostReport,
  followUp: useGetFollowUpReport,
  hospitalIncome: useGetHospitalIncomeReport,
  noShowAppointments: useGetNoShowAppointmentsReport,
  prescription: useGetPrescriptionReport,
  referral: useGetReferralReport,
  transactions: useGetTransactionsReport,
  // visitSummaryNotes: useGetVisitSummaryNotesReport,
  orders: useGetOrdersReport,
  images: useGetImagesReport,
  experiments: useGetExperimentsReport,
  "medical-history": useGetMedicalHistoryReport,
  medicalServiceProvider: useGetMedicalServiceReport,
  medicalServiceCenters: useGetMedicalServiceCentersReport,
  medicalDoctors: useGetMedicalServiceDoctorsReport,
  medicalServiceAssign: useGetMedicalServiceAssignReport,
  medicalServiceServices: useGetMedicalServiceServicesReport,
};

const reportsFunc: Record<
  IReportsType,
  IAxiosFn<
    IReportDownloadParams,
    AxiosResponse<IApiResponse<IReportDownloadEndpoint>>
  >
> = {
  allPatients: getAllPatientsReport,
  appointments: getAppointmentsReport,
  doctorsIncome: getDoctorsIncomeReport,
  doctorVisitCost: getDoctorVisitCostReport,
  systemAccounting: getSystemAccountingReport,
  followUp: getFollowUpReport,
  hospitalIncome: getHospitalIncomeReport,
  noShowAppointments: getNoShowAppointmentsReport,
  prescription: getPrescriptionReport,
  referral: getReferralReport,
  transactions: getTransactionsReport,
  // visitSummaryNotes: getVisitSummaryNotesReport,
  orders: getOrdersReport,
  experiments: getExperimentsReport,
  images: getImagesReport,
  "medical-history": getMedicalHistoryReport,
  medicalServiceProvider: getMedicalServiceReport,
  medicalServiceCenters: getMedicalServiceCentersReport,
  medicalDoctors: getMedicalServiceDoctorsReport,
  medicalServiceAssign: getMedicalServiceAssignReport,
  medicalServiceServices: getMedicalServiceServicesReport,
};

export const useDownloadReport = (
  type: IReportsType | undefined,
  params?: object,
) => {
  const notifyPromise =
    useCustomNotifyPromise<
      AxiosResponse<IApiResponse<IReportDownloadEndpoint>>
    >();
  const func = useMemo(() => (type ? reportsFunc[type] : undefined), [type]);

  const onDownload = useCallback(
    async (extraParams?: { customData?: object }) => {
      if (!func) return;
      const data = await notifyPromise(
        func({ ...extraParams?.customData, ...params, type: "excel" }),
      );

      downloadFile(data?.data?.data);
    },
    [func, notifyPromise, params],
  );

  const onPrint = useCallback(
    async (extraParams?: { customData?: object }) => {
      if (!func) return;
      const data = await notifyPromise(
        func({ ...extraParams?.customData, ...params, type: "pdf" }),
      );

      downloadFile(data?.data?.data);
    },
    [func, notifyPromise, params],
  );

  return useMemo(() => {
    if (!type || !func) return { onDownload: undefined, onPrint: undefined };
    return { onPrint, onDownload };
  }, [func, onDownload, onPrint, type]);
};
